import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { dialogs } from "../../context/DialogContext";
import api from "../../services/api";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import CamposNotariaParteExpediente from "./CamposNotariaParteExpediente";
import {
  CAMPOS_NOTARIA,
  datosNotariaDesdeCliente,
  datosNotariaDesdeContacto,
  TIPOS_DOCUMENTO,
} from "../../utils/parteExpedienteCampos";
import { camposDireccionDesdePlaces, separarDireccionCompuesta } from "../../utils/googlePlacesAddressLine";

function parseDireccionParte(direccion, fallback = {}) {
  const d = String(direccion || "").trim();
  if (!d) {
    return {
      direccion: "",
      poblacion: fallback.poblacion || "",
      provincia: fallback.provincia || "",
      codigo_postal: fallback.codigo_postal || "",
    };
  }
  if (!fallback.poblacion && !fallback.provincia && d.includes(",")) {
    const sep = separarDireccionCompuesta(d);
    if (sep.poblacion || sep.provincia) return sep;
  }
  return {
    direccion: d,
    poblacion: fallback.poblacion || "",
    provincia: fallback.provincia || "",
    codigo_postal: fallback.codigo_postal || "",
  };
}

function tint(accent, alpha, isDark) {
  const a = isDark ? Math.min(alpha + 0.06, 0.35) : alpha;
  return `${accent}${Math.round(a * 255).toString(16).padStart(2, "0")}`;
}

function BloqueSeccion({ icon, titulo, accent, isDark, children, style }) {
  const border = tint(accent, 0.22, isDark);
  const headBg = tint(accent, isDark ? 0.14 : 0.1, isDark);
  const bodyBg = tint(accent, isDark ? 0.06 : 0.04, isDark);
  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: bodyBg,
      minWidth: 0,
      ...style,
    }}
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 10px",
        borderBottom: `1px solid ${border}`,
        background: headBg,
      }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
        <div style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: accent,
        }}
        >
          {titulo}
        </div>
      </div>
      <div style={{ padding: "8px 10px 10px" }}>{children}</div>
    </div>
  );
}

function Campo({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: span === 2 ? "span 2" : span === 3 ? "span 3" : "span 1" }}>
      {label}
      {children}
    </div>
  );
}

function buildFormInicial(parte, esComprador, ubicacionExpediente) {
  const ubFallback = {
    poblacion: ubicacionExpediente?.poblacion || "",
    provincia: ubicacionExpediente?.provincia || "",
    codigo_postal: ubicacionExpediente?.codigo_postal || "",
  };
  const base = parseDireccionParte(parte.direccion, {
    poblacion: parte.poblacion || ubFallback.poblacion,
    provincia: parte.provincia || ubFallback.provincia,
    codigo_postal: parte.codigo_postal || ubFallback.codigo_postal,
  });
  return {
    ...parte,
    nombre: parte.nombre || parte.cli_nombre || "",
    apellidos: parte.apellidos || parte.cli_apellido || "",
    tipo_documento: parte.tipo_documento || "DNI",
    dni: parte.dni || "",
    telefono: parte.telefono || "",
    email: parte.email || "",
    ...base,
  };
}

export default function ModalEdicionParteExpediente({
  isOpen,
  tipo,
  parte,
  ubicacionExpediente,
  onClose,
  onGuardado,
}) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#f1f5f9" : t.textPrimary;
  const esComprador = tipo === "comprador";
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();

  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [placesContext, setPlacesContext] = useState(null);

  useEffect(() => {
    if (!isOpen || !parte) return undefined;
    let alive = true;

    (async () => {
      let next = buildFormInicial(parte, esComprador, ubicacionExpediente);
      try {
        if (esComprador && parte.cliente_id) {
          const r = await api.get(`/clientes/${parte.cliente_id}`);
          const c = r.data?.data || r.data;
          if (c) next = mergeComercial(next, c, datosNotariaDesdeCliente(c), ubicacionExpediente);
        } else if (!esComprador && parte.contacto_id) {
          const r = await api.get(`/contactos/${parte.contacto_id}`);
          const c = r.data?.data || r.data;
          if (c) next = mergeComercial(next, c, datosNotariaDesdeContacto(c), ubicacionExpediente);
        }
      } catch { /* fila local sin ficha CRM */ }

      if (!next.poblacion?.trim() && ubicacionExpediente?.poblacion) {
        next.poblacion = ubicacionExpediente.poblacion;
      }
      if (!next.provincia?.trim() && ubicacionExpediente?.provincia) {
        next.provincia = ubicacionExpediente.provincia;
      }

      if (alive) setForm(next);
    })();

    return () => { alive = false; };
  }, [isOpen, parte, esComprador, ubicacionExpediente]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    const pob = (form.poblacion || "").trim();
    const prov = (form.provincia || "").trim();
    if (!pob) {
      setPlacesContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pob, provincia: prov, pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesContext(null);
      } catch {
        if (alive) setPlacesContext(null);
      }
    })();
    return () => { alive = false; };
  }, [isOpen, form.poblacion, form.provincia]);

  if (!isOpen) return null;

  const accent = esComprador ? "#2563eb" : "#7c3aed";
  const accentLight = esComprador ? "#60a5fa" : "#a78bfa";
  const accentEscritura = isDark ? "#fbbf24" : "#b45309";
  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 };
  const grid3 = { display: "grid", gridTemplateColumns: "76px 1fr 1fr", gap: 6 };

  const inp = {
    padding: "5px 8px",
    borderRadius: 6,
    fontSize: 12,
    width: "100%",
    background: isDark ? "#0f172a" : "#fff",
    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
    color: tx,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };
  const sel = { ...inp, cursor: "pointer" };
  const lbl = {
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: isDark ? "#94a3b8" : "#64748b",
    display: "block",
    marginBottom: 2,
  };

  const nombreMostrar = [form.nombre, form.apellidos].filter(Boolean).join(" ").trim();

  const guardar = async () => {
    if (!form.nombre?.trim() && !form.apellidos?.trim()) {
      await dialogs.alert("Indica nombre o apellidos.");
      return;
    }
    setGuardando(true);
    try {
      if (esComprador && parte.cliente_id) {
        await api.patch(`/clientes/${parte.cliente_id}`, {
          nombre: (form.nombre || "").trim(),
          apellido: (form.apellidos || "").trim(),
          telefono: (form.telefono || "").trim(),
          email: (form.email || "").trim(),
          dni: (form.dni || "").trim(),
          direccion: (form.direccion || "").trim(),
          poblacion: (form.poblacion || "").trim(),
          provincia: (form.provincia || "").trim(),
          codigo_postal: (form.codigo_postal || "").trim(),
        });
      } else if (!esComprador && parte.contacto_id) {
        await api.patch(`/contactos/${parte.contacto_id}`, {
          nombre: (form.nombre || "").trim(),
          apellidos: (form.apellidos || "").trim(),
          telefono: (form.telefono || "").trim(),
          email: (form.email || "").trim(),
          dni_nie: (form.dni || "").trim(),
          direccion: (form.direccion || "").trim(),
          poblacion: (form.poblacion || "").trim(),
          provincia: (form.provincia || "").trim(),
        });
      }

      const datos = {
        nombre: (form.nombre || "").trim(),
        apellidos: (form.apellidos || "").trim(),
        tipo_documento: form.tipo_documento || "DNI",
        dni: (form.dni || "").trim(),
        direccion: (form.direccion || "").trim(),
        poblacion: (form.poblacion || "").trim(),
        provincia: (form.provincia || "").trim(),
        codigo_postal: (form.codigo_postal || "").trim(),
        estado_civil: form.estado_civil || "",
        regimen_matrimonial: form.regimen_matrimonial || "",
        profesion: (form.profesion || "").trim(),
        sexo: form.sexo || "",
        nacionalidad: (form.nacionalidad || "").trim(),
      };
      if (esComprador) {
        datos.porcentaje = form.porcentaje ?? "";
      } else {
        datos.nuda_propiedad = form.nuda_propiedad ?? "";
        datos.pleno_dominio = form.pleno_dominio ?? "";
        datos.usufructo = form.usufructo ?? "";
      }
      onGuardado?.(datos);
      onClose();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const titulo = esComprador ? "Editar comprador" : "Editar propietario";
  const iconoRol = esComprador ? "🛒" : "🏠";

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "rgba(0,0,0,0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
        padding: 12,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !guardando) onClose(); }}
    >
      <div
        ref={dragRef}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 760,
          maxWidth: "96vw",
          display: "flex",
          flexDirection: "column",
          background: isDark ? "#1a2030" : t.bgCard,
          borderRadius: 14,
          boxShadow: `0 28px 70px rgba(0,0,0,0.45), 0 0 0 1px ${tint(accent, 0.15, isDark)}`,
          ...panelPositionStyle,
        }}
      >
        <div style={{ height: 4, flexShrink: 0, background: `linear-gradient(90deg, ${accentLight}, ${accent}, ${accentLight})` }} />

        <div
          onMouseDown={startDrag}
          style={{
            padding: "10px 14px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexShrink: 0,
            cursor: "grab",
            userSelect: "none",
            borderBottom: `1px solid ${isDark ? "#2d3748" : "#e5e7eb"}`,
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: tint(accent, 0.18, isDark),
              border: `1px solid ${tint(accent, 0.3, isDark)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
            }}
            >
              {iconoRol}
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: tx, letterSpacing: "-0.02em" }}>
                {titulo}
              </h2>
              {nombreMostrar && (
                <p style={{ margin: "1px 0 0", fontSize: 11, color: isDark ? "#94a3b8" : t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {nombreMostrar}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
              background: isDark ? "#252b3a" : "#f3f4f6",
              cursor: "pointer",
              fontSize: 14,
              color: isDark ? "#94a3b8" : "#6b7280",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignItems: "start" }}>
            <BloqueSeccion icon="📇" titulo="Datos comerciales" accent={accent} isDark={isDark}>
              <div style={grid2}>
                <Campo label={<label style={lbl}>Nombre *</label>}>
                  <input style={inp} value={form.nombre || ""} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Apellidos</label>}>
                  <input style={inp} value={form.apellidos || ""} onChange={(e) => setForm((f) => ({ ...f, apellidos: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Teléfono</label>}>
                  <input style={inp} value={form.telefono || ""} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Email</label>}>
                  <input style={inp} type="email" value={form.email || ""} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Tipo doc.</label>}>
                  <select style={sel} value={form.tipo_documento || "DNI"} onChange={(e) => setForm((f) => ({ ...f, tipo_documento: e.target.value }))}>
                    {TIPOS_DOCUMENTO.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </Campo>
                <Campo label={<label style={lbl}>Nº documento</label>}>
                  <input style={inp} value={form.dni || ""} onChange={(e) => setForm((f) => ({ ...f, dni: e.target.value }))} />
                </Campo>
              </div>
            </BloqueSeccion>

            <BloqueSeccion icon="📍" titulo="Ubicación" accent={accentLight} isDark={isDark}>
              <div style={grid3}>
                <Campo label={<label style={lbl}>CP</label>}>
                  <input style={inp} value={form.codigo_postal || ""} onChange={(e) => setForm((f) => ({ ...f, codigo_postal: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Población</label>}>
                  <input style={inp} value={form.poblacion || ""} onChange={(e) => setForm((f) => ({ ...f, poblacion: e.target.value }))} />
                </Campo>
                <Campo label={<label style={lbl}>Provincia</label>}>
                  <input style={inp} value={form.provincia || ""} onChange={(e) => setForm((f) => ({ ...f, provincia: e.target.value }))} />
                </Campo>
              </div>
              <div style={{ marginTop: 6 }}>
                <label style={lbl}>Dirección</label>
                <AddressAutocompleteInput
                  value={form.direccion || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, direccion: v }))}
                  onPlaceAddressResolved={(parts) => setForm((f) => ({
                    ...f,
                    ...camposDireccionDesdePlaces(parts, f),
                  }))}
                  style={inp}
                  placesLocalityContext={placesContext}
                  placeholder="Calle y número…"
                />
              </div>
            </BloqueSeccion>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: esComprador ? "140px 1fr" : "1fr 1fr", gap: 8, alignItems: "start" }}>
            <BloqueSeccion
              icon={esComprador ? "📊" : "🏛️"}
              titulo={esComprador ? "Expediente · % compra" : "Expediente · titularidad"}
              accent={accentLight}
              isDark={isDark}
            >
              {esComprador ? (
                <div>
                  <label style={lbl}>% compra</label>
                  <input
                    style={{ ...inp, textAlign: "right", fontWeight: 800, color: accent }}
                    value={form.porcentaje ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, porcentaje: e.target.value }))}
                    placeholder="100"
                  />
                </div>
              ) : (
                <div style={{ ...grid2, gridTemplateColumns: "1fr 1fr 1fr" }}>
                  {[
                    { key: "nuda_propiedad", label: "Nuda" },
                    { key: "pleno_dominio", label: "Pleno" },
                    { key: "usufructo", label: "Usuf." },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label style={lbl}>{label}</label>
                      <input
                        style={{ ...inp, textAlign: "right", fontWeight: 700 }}
                        value={form[key] ?? ""}
                        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </BloqueSeccion>

            <BloqueSeccion icon="📜" titulo="Escritura notarial" accent={accentEscritura} isDark={isDark}>
              <CamposNotariaParteExpediente
                form={form}
                setForm={setForm}
                inp={inp}
                sel={sel}
                lbl={lbl}
                sinDireccion
                compact
              />
            </BloqueSeccion>
          </div>
        </div>

        <div style={{
          padding: "8px 12px 10px",
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          flexShrink: 0,
          borderTop: `1px solid ${isDark ? "#2d3748" : "#e5e7eb"}`,
          background: isDark ? "#141820" : "#fafafa",
        }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            style={{
              padding: "7px 18px",
              borderRadius: 8,
              border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
              background: "transparent",
              color: isDark ? "#94a3b8" : "#64748b",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando}
            style={{
              padding: "7px 22px",
              borderRadius: 8,
              border: "none",
              background: `linear-gradient(135deg, ${accent}, ${esComprador ? "#1d4ed8" : "#6d28d9"})`,
              color: "#fff",
              cursor: guardando ? "wait" : "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 700,
              boxShadow: `0 3px 10px ${tint(accent, 0.3, false)}`,
              opacity: guardando ? 0.75 : 1,
            }}
          >
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function mergeComercial(f, registro, notariaExtra, ubicacionExpediente) {
  const merged = { ...f };
  const parsed = parseDireccionParte(registro.direccion || merged.direccion || "", {
    poblacion: registro.poblacion || merged.poblacion || ubicacionExpediente?.poblacion,
    provincia: registro.provincia || merged.provincia || ubicacionExpediente?.provincia,
    codigo_postal: registro.codigo_postal || merged.codigo_postal,
  });
  const comercial = {
    nombre: registro.nombre || "",
    apellidos: registro.apellido || registro.apellidos || "",
    telefono: registro.telefono || registro.whatsapp || "",
    email: registro.email || "",
    dni: registro.dni || registro.dni_nie || "",
    direccion: parsed.direccion || merged.direccion || "",
    poblacion: registro.poblacion || parsed.poblacion || ubicacionExpediente?.poblacion || "",
    provincia: registro.provincia || parsed.provincia || ubicacionExpediente?.provincia || "",
    codigo_postal: registro.codigo_postal || parsed.codigo_postal || "",
  };
  for (const [k, v] of Object.entries(comercial)) {
    if (!String(merged[k] || "").trim() && String(v || "").trim()) merged[k] = v;
  }
  for (const k of ["nombre", "apellidos", "tipo_documento", "dni", ...CAMPOS_NOTARIA]) {
    if (!String(merged[k] || "").trim() && notariaExtra[k]) merged[k] = notariaExtra[k];
  }
  if (!merged.tipo_documento) merged.tipo_documento = notariaExtra.tipo_documento || "DNI";
  return merged;
}
